import React, { useEffect, useState } from 'react';
import Navigation from '../components/Navigation';
import LoadingSpinner from '../components/LoadingSpinner';
import './SubscriptionPlansPage.css';

interface SubscriptionPlansPageProps {
  showNavigation?: boolean;
}

const SubscriptionPlansPage: React.FC<SubscriptionPlansPageProps> = ({ showNavigation = true }) => {
  const [scriptLoaded, setScriptLoaded] = useState(false);

  useEffect(() => {
    // Check if script is already loaded
    if (document.querySelector('script[src="https://js.stripe.com/v3/pricing-table.js"]')) {
      setScriptLoaded(true);
      return;
    }

    // Load Stripe pricing table script
    const script = document.createElement('script');
    script.src = 'https://js.stripe.com/v3/pricing-table.js';
    script.async = true;
    script.onload = () => {
      setScriptLoaded(true);
      
      // Inject dark mode styles into the page since Stripe uses Shadow DOM
      // This is a workaround to style the pricing table in dark mode
      const isDarkMode = document.documentElement.classList.contains('dark');
      if (isDarkMode) {
        const style = document.createElement('style');
        style.id = 'stripe-pricing-table-dark-mode';
        style.textContent = `
          stripe-pricing-table {
            --stripe-pricing-table-background: rgb(17 24 39);
            --stripe-pricing-table-text: rgb(243 244 246);
            --stripe-pricing-table-border: rgb(55 65 81);
            --stripe-pricing-table-button-background: rgb(37 99 235);
            --stripe-pricing-table-button-text: white;
            --stripe-pricing-table-button-hover: rgb(29 78 216);
            --stripe-pricing-table-card-background: rgb(31 41 55);
            --stripe-pricing-table-price-text: rgb(243 244 246);
            --stripe-pricing-table-description-text: rgb(209 213 219);
          }
        `;
        document.head.appendChild(style);
      }
    };
    script.onerror = () => {
      console.error('Failed to load Stripe pricing table script');
    };
    document.body.appendChild(script);

    // Watch for dark mode changes
    const observer = new MutationObserver(() => {
      const isDarkMode = document.documentElement.classList.contains('dark');
      let style = document.getElementById('stripe-pricing-table-dark-mode');
      
      if (isDarkMode && !style) {
        style = document.createElement('style');
        style.id = 'stripe-pricing-table-dark-mode';
        style.textContent = `
          stripe-pricing-table {
            --stripe-pricing-table-background: rgb(17 24 39);
            --stripe-pricing-table-text: rgb(243 244 246);
            --stripe-pricing-table-border: rgb(55 65 81);
            --stripe-pricing-table-button-background: rgb(37 99 235);
            --stripe-pricing-table-button-text: white;
            --stripe-pricing-table-button-hover: rgb(29 78 216);
            --stripe-pricing-table-card-background: rgb(31 41 55);
            --stripe-pricing-table-price-text: rgb(243 244 246);
            --stripe-pricing-table-description-text: rgb(209 213 219);
          }
        `;
        document.head.appendChild(style);
      } else if (!isDarkMode && style) {
        style.remove();
      }
    });

    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['class']
    });

    return () => {
      observer.disconnect();
      const style = document.getElementById('stripe-pricing-table-dark-mode');
      if (style) {
        style.remove();
      }
    };
  }, []);

  // Get Stripe keys from environment variables (required in production)
  const pricingTableId = process.env.REACT_APP_STRIPE_PRICING_TABLE_ID;
  const publishableKey = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY;

  if (!pricingTableId || !publishableKey) {
    console.error('Stripe configuration missing: REACT_APP_STRIPE_PRICING_TABLE_ID and REACT_APP_STRIPE_PUBLISHABLE_KEY must be set');
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 dark:from-gray-900 dark:to-gray-800">
        {showNavigation && <Navigation />}
        <div className="w-full flex items-center justify-center py-20">
          <div className="text-center">
            <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-4">
              Configuration Error
            </h1>
            <p className="text-gray-600 dark:text-gray-400">
              Stripe is not properly configured. Please contact support.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 dark:from-gray-900 dark:to-gray-800">
      {showNavigation && <Navigation />}
      <div className="w-full">
        {/* Header */}
        {showNavigation && (
          <div className="text-center py-10 px-4 bg-transparent">
            <h1 className="text-4xl font-bold text-gray-900 dark:text-gray-100 mb-3">
              Choose Your Plan
            </h1>
            <p className="text-lg text-gray-600 dark:text-gray-400 max-w-2xl mx-auto">
              Select the subscription that fits your needs
            </p>
          </div>
        )}

        {/* Stripe Pricing Table - Minimal wrapper, no extra containers */}
        {scriptLoaded ? (
          <div className="w-full pb-16 px-4">
            {React.createElement('stripe-pricing-table', {
              'pricing-table-id': pricingTableId,
              'publishable-key': publishableKey,
            })}
          </div>
        ) : (
          <div className="flex justify-center py-20">
            <LoadingSpinner />
          </div>
        )}
      </div>
    </div>
  );
};

export default SubscriptionPlansPage;
